import {
  Delete as DeleteIcon,
  PlayCircleOutline as PlayCircleOutlineIcon,
  Replay as ReplayIcon,
} from '@mui/icons-material';
import {
  Box,
  Button,
  Card,
  CardContent,
  Chip,
  CircularProgress,
  IconButton,
  List,
  ListItem,
  ListItemButton,
  Typography,
} from '@mui/material';
import {Job, JobStatus} from '@cp/model';
import {env} from '../../../config/env';
import formatDate from '../../../utils/time/formatDate';

export interface AdminJobsListProps {
  jobs: Job[];
  deleteLoading: boolean;
  resumeLoading: boolean;
  increaseRunLimitLoading?: boolean;
  onJobClick: (job: Job) => void;
  onDeleteClick: (event: React.MouseEvent, jobId: string) => void;
  onResumeClick: (event: React.MouseEvent, jobId: string) => void;
  onIncreaseRunLimit?: (event: React.MouseEvent, jobId: string, amount: number) => void;
  onTriggerJobProcessor?: (event: React.MouseEvent, jobId: string) => void;
  triggeringJobId?: string | null;
}

const getStatusColor = (status: JobStatus) => {
  switch (status) {
    case JobStatus.COMPLETED:
      return 'success';
    case JobStatus.RUNNING:
      return 'primary';
    case JobStatus.FAILED:
      return 'error';
    case JobStatus.PENDING:
      return 'default';
    case JobStatus.CANCELLED:
      return 'default';
    default:
      return 'default';
  }
};

const RUN_LIMIT_AMOUNTS = [10, 100, 1000] as const;

export default function AdminJobsList({
  jobs,
  deleteLoading,
  resumeLoading,
  increaseRunLimitLoading = false,
  onJobClick,
  onDeleteClick,
  onResumeClick,
  onIncreaseRunLimit,
  onTriggerJobProcessor,
  triggeringJobId,
}: AdminJobsListProps) {
  if (jobs.length === 0) {
    return (
      <Card>
        <CardContent sx={{p: 0}}>
          <Box sx={{p: 3, textAlign: 'center'}}>
            <Typography color="text.secondary">No jobs found</Typography>
          </Box>
        </CardContent>
      </Card>
    );
  }

  return (
    <Card>
      <CardContent sx={{p: 0}}>
        <List>
          {jobs.map((job) => (
            <ListItem key={job.id} disablePadding>
              <ListItemButton onClick={() => onJobClick(job)}>
                <Box sx={{display: 'flex', flexDirection: 'column', width: '100%', pr: 1}}>
                  <Box sx={{display: 'flex', alignItems: 'center', gap: 1, mb: 1}}>
                    <Typography variant="subtitle1">{job.title}</Typography>
                    <Chip label={job.status} color={getStatusColor(job.status)} size="small" />
                    {job.status === JobStatus.RUNNING && job.progress !== null && job.progress !== undefined && (
                      <Box sx={{display: 'flex', alignItems: 'center', gap: 1}}>
                        <CircularProgress size={16} variant="determinate" value={job.progress} />
                        <Typography variant="caption">{job.progress}%</Typography>
                      </Box>
                    )}
                  </Box>
                  <Box sx={{display: 'flex', flexDirection: 'column', gap: 0.5}}>
                    <Typography variant="body2" color="text.secondary">
                      Created: {formatDate(job.createdAt)}
                    </Typography>
                    {job.startedAt && (
                      <Typography variant="body2" color="text.secondary">
                        Started: {formatDate(job.startedAt)}
                      </Typography>
                    )}
                    {job.completedAt && (
                      <Typography variant="body2" color="text.secondary">
                        Completed: {formatDate(job.completedAt)}
                      </Typography>
                    )}
                    {job.description && (
                      <Typography variant="body2" color="text.secondary">
                        {job.description}
                      </Typography>
                    )}
                    {job.messages && job.messages.length > 0 && (
                      <Typography variant="body2" color="text.secondary" sx={{fontStyle: 'italic'}}>
                        Last message: {job.messages[job.messages.length - 1]}
                      </Typography>
                    )}
                  </Box>
                </Box>
                <Box sx={{display: 'flex', alignItems: 'center', gap: 0.5, flexWrap: 'wrap'}}>
                  {onIncreaseRunLimit &&
                    RUN_LIMIT_AMOUNTS.map((amount) => (
                      <Button
                        key={amount}
                        size="small"
                        variant="outlined"
                        disabled={increaseRunLimitLoading}
                        onClick={(e) => onIncreaseRunLimit(e, job.id, amount)}
                        sx={{minWidth: 0, px: 1}}
                      >
                        +{amount}
                      </Button>
                    ))}
                  {(job.status === JobStatus.FAILED || job.status === JobStatus.PARTIALLY_COMPLETED) && (
                    <IconButton
                      edge="end"
                      onClick={(e) => onResumeClick(e, job.id)}
                      disabled={resumeLoading}
                      color="primary"
                      size="small"
                    >
                      <ReplayIcon />
                    </IconButton>
                  )}
                  {env.isDev && onTriggerJobProcessor && (
                    <IconButton
                      edge="end"
                      onClick={(e) => onTriggerJobProcessor(e, job.id)}
                      disabled={triggeringJobId === job.id}
                      color="primary"
                      size="small"
                      title="Trigger Job Processor"
                    >
                      {triggeringJobId === job.id ? <CircularProgress size={20} /> : <PlayCircleOutlineIcon />}
                    </IconButton>
                  )}
                  <IconButton
                    edge="end"
                    onClick={(e) => onDeleteClick(e, job.id)}
                    disabled={deleteLoading}
                    color="error"
                    size="small"
                  >
                    <DeleteIcon />
                  </IconButton>
                </Box>
              </ListItemButton>
            </ListItem>
          ))}
        </List>
      </CardContent>
    </Card>
  );
}
